import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionHeading, { SECTION_LABEL_FONT_SIZE } from '@/components/base/SectionHeading';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import {
  COMPANY_FOUNDED_YEAR,
  anniversaryOrdinalSuffix,
  getAnniversaryMilestone,
  getPreviewTimelineYear,
  getTimelineYear,
} from '@/utils/companyTimeline';

const keywords = [
  { label: '誠実', sub: 'Integrity' },
  { label: '技術', sub: 'Technology' },
  { label: '地域', sub: 'Community' },
];

/** 本文 16px 基準: キーワード和字はさらに大きく */
const BODY_FONT_SIZE = '16px';
const KEYWORD_LABEL_FONT_SIZE = 'clamp(22px, 1.45rem, 26px)';

const FOUNDED_YEAR = COMPANY_FOUNDED_YEAR;
/** 設立年 → 現在年: 序盤・終盤はゆっくり、中盤で加速 */
const FOUNDED_YEAR_COUNT_MS = 2800;

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

function useFoundedYearCount(active: boolean, endYear: number): number {
  const [year, setYear] = useState(FOUNDED_YEAR);
  const startedRef = useRef(false);

  useEffect(() => {
    startedRef.current = false;
    setYear(FOUNDED_YEAR);
  }, [endYear]);

  useEffect(() => {
    if (!active || startedRef.current) return;
    startedRef.current = true;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setYear(endYear);
      return;
    }

    let raf = 0;
    const t0 = performance.now();

    const tick = (now: number) => {
      const progress = Math.min(1, (now - t0) / FOUNDED_YEAR_COUNT_MS);
      const eased = easeInOutCubic(progress);
      const next = Math.round(FOUNDED_YEAR + (endYear - FOUNDED_YEAR) * eased);
      setYear(next);
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        setYear(endYear);
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, endYear]);

  return year;
}

function FoundedYearDisplay({ active, endYear }: { active: boolean; endYear: number }) {
  const year = useFoundedYearCount(active, endYear);

  return (
    <>
      <div
        className="font-black tabular-nums leading-none"
        style={{
          fontSize: '64px',
          color: '#111111',
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '-0.05em',
          lineHeight: 1,
          minWidth: '4.5ch',
        }}
        aria-live="polite"
        aria-label={`設立 ${FOUNDED_YEAR}年から ${endYear}年まで`}
      >
        {year}
      </div>
      <p
        className="mt-1"
        style={{
          fontSize: '14px',
          color: '#888',
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
        }}
      >
        Est. {FOUNDED_YEAR}
      </p>
    </>
  );
}

function AnniversaryBadge({ milestone }: { milestone: number }) {
  const suffix = anniversaryOrdinalSuffix(milestone);

  return (
    <div className="absolute bottom-6 right-6 z-10 text-right">
      <div
        className="font-black leading-none"
        style={{
          fontSize: '72px',
          color: '#111111',
          fontFamily: '"Inter", sans-serif',
          letterSpacing: '-0.05em',
          lineHeight: 1,
        }}
      >
        {milestone}{' '}
        <span style={{ fontSize: '24px', letterSpacing: '0.12em' }}>{suffix}</span>
      </div>
      <div
        className="mt-1"
        style={{
          fontSize: '24px',
          color: '#FF0000',
          fontFamily: '"Inter", sans-serif',
          fontWeight: 'bold',
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
        }}
      >
        Anniversary
      </div>
    </div>
  );
}

export default function AboutSection({ showProfileLink = true }: { showProfileLink?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const timelineYear = getTimelineYear();
  const previewYear = getPreviewTimelineYear();
  const anniversaryMilestone = getAnniversaryMilestone(timelineYear);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <SubPageSection id="about" spacing="wide">
      {previewYear != null && (
        <p
          className="mx-auto mb-4 text-center text-[11px] text-amber-800/90"
          style={{ fontFamily: '"Noto Sans JP", sans-serif' }}
        >
          表示確認用: {previewYear}年としてシミュレート中（URL の <code className="text-[10px]">?cadecSimYear=</code>{' '}
          を外すと通常表示）
        </p>
      )}
      <div ref={ref}>
        <div
          className={`mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <SectionHeading label="ABOUT" title="会社について" tone="dark" />
        </div>

        <SubPageSurface className="p-8 md:p-12 lg:p-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-28 items-center">
          {/* Left */}
          <div
            className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <p className="leading-relaxed mb-5" style={{ fontSize: BODY_FONT_SIZE, lineHeight: '2.2', fontFamily: '"Noto Sans JP", sans-serif', color: '#555555' }}>
              わたしたち株式会社キャデックは平成3年5月、企業グループ8社によりソフト産業の技術転移、育成を目的とし、青森県及び八戸市の発展と人材育成を担い、青森県並びに八戸市誘致企業として設立されました。
            </p>
            <p className="leading-relaxed mb-10" style={{ fontSize: BODY_FONT_SIZE, lineHeight: '2.2', fontFamily: '"Noto Sans JP", sans-serif', color: '#555555' }}>
              以来、設計活動の柔軟化と合理化を目指し技術系である構造部及び設計部をもった総合意匠設計事務所という形で、設計業務を進めてきました。これからも常に誠意と責任を持って仕事に接し、建築を通じて地域社会に貢献して参りたいと考えています。
            </p>

            {/* Keywords */}
            <div className="flex gap-6 mb-10">
              {keywords.map((kw) => (
                <div key={kw.label} className="flex flex-col">
                  <span
                    className="font-bold text-gray-800 mb-0.5"
                    style={{ fontSize: KEYWORD_LABEL_FONT_SIZE, fontFamily: '"Noto Sans JP", sans-serif' }}
                  >
                    {kw.label}
                  </span>
                  <span
                    className="text-gray-400"
                    style={{
                      fontSize: SECTION_LABEL_FONT_SIZE,
                      fontFamily: '"Inter", sans-serif',
                      letterSpacing: '0.08em',
                    }}
                  >
                    {kw.sub}
                  </span>
                  <div className="mt-2 w-6 h-px bg-gray-200" />
                </div>
              ))}
            </div>

            {showProfileLink && (
              <Link
                to="/company"
                className="inline-flex items-center gap-3 cursor-pointer whitespace-nowrap group"
                style={{
                  fontSize: '15px',
                  letterSpacing: '0.18em',
                  color: '#111111',
                  fontFamily: '"Inter", sans-serif',
                  borderBottom: '1px solid #111111',
                  paddingBottom: '4px',
                }}
              >
                COMPANY PROFILE
                <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-200" style={{ fontSize: '16px' }} />
              </Link>
            )}
          </div>

          {/* Right - Blueprint visual */}
          <div
            className={`relative transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="relative w-full aspect-square max-w-md mx-auto overflow-hidden" style={{ background: 'transparent' }}>

              {/* Blueprint floor plan — cropped, no frame, placed raw */}
              <svg
                className="absolute"
                style={{
                  top: '-18%',
                  left: '-12%',
                  width: '145%',
                  height: '145%',
                  opacity: 0.18,
                }}
                viewBox="0 0 900 900"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* ===== 外壁（太い） ===== */}
                <rect x="80" y="60" width="740" height="780" stroke="#111" strokeWidth="14" />

                {/* ===== 主要間仕切り壁 ===== */}
                {/* 横 */}
                <line x1="80" y1="280" x2="820" y2="280" stroke="#111" strokeWidth="10" />
                <line x1="80" y1="560" x2="820" y2="560" stroke="#111" strokeWidth="10" />
                {/* 縦 */}
                <line x1="340" y1="60" x2="340" y2="840" stroke="#111" strokeWidth="10" />
                <line x1="580" y1="280" x2="580" y2="840" stroke="#111" strokeWidth="10" />
                {/* 廊下仕切り */}
                <line x1="340" y1="170" x2="820" y2="170" stroke="#111" strokeWidth="6" />

                {/* ===== ドア（開き弧） ===== */}
                {/* 左上室 → 廊下 */}
                <line x1="80" y1="200" x2="80" y2="260" stroke="#111" strokeWidth="6" />
                <line x1="80" y1="200" x2="140" y2="200" stroke="#111" strokeWidth="6" />
                <path d="M80 200 A60 60 0 0 1 140 200" stroke="#111" strokeWidth="3" fill="none" />

                {/* 廊下 → 右上室A */}
                <line x1="420" y1="170" x2="480" y2="170" stroke="#111" strokeWidth="6" />
                <path d="M420 170 A60 60 0 0 0 420 230" stroke="#111" strokeWidth="3" fill="none" />

                {/* 廊下 → 右上室B */}
                <line x1="660" y1="170" x2="720" y2="170" stroke="#111" strokeWidth="6" />
                <path d="M660 170 A60 60 0 0 0 660 230" stroke="#111" strokeWidth="3" fill="none" />

                {/* 左中室 */}
                <line x1="80" y1="400" x2="80" y2="460" stroke="#111" strokeWidth="6" />
                <line x1="80" y1="400" x2="140" y2="400" stroke="#111" strokeWidth="6" />
                <path d="M80 400 A60 60 0 0 1 140 400" stroke="#111" strokeWidth="3" fill="none" />

                {/* 左下室 */}
                <line x1="80" y1="660" x2="80" y2="720" stroke="#111" strokeWidth="6" />
                <line x1="80" y1="660" x2="140" y2="660" stroke="#111" strokeWidth="6" />
                <path d="M80 660 A60 60 0 0 1 140 660" stroke="#111" strokeWidth="3" fill="none" />

                {/* 右下室A */}
                <line x1="340" y1="660" x2="400" y2="660" stroke="#111" strokeWidth="6" />
                <path d="M400 660 A60 60 0 0 1 400 720" stroke="#111" strokeWidth="3" fill="none" />

                {/* 右下室B */}
                <line x1="580" y1="660" x2="640" y2="660" stroke="#111" strokeWidth="6" />
                <path d="M640 660 A60 60 0 0 1 640 720" stroke="#111" strokeWidth="3" fill="none" />

                {/* ===== 窓記号（壁に埋め込み） ===== */}
                {/* 上壁 */}
                <line x1="120" y1="60" x2="280" y2="60" stroke="#111" strokeWidth="14" />
                <line x1="120" y1="48" x2="120" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="280" y1="48" x2="280" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="120" y1="54" x2="280" y2="54" stroke="#111" strokeWidth="2" />
                <line x1="120" y1="66" x2="280" y2="66" stroke="#111" strokeWidth="2" />

                <line x1="400" y1="60" x2="540" y2="60" stroke="#111" strokeWidth="14" />
                <line x1="400" y1="48" x2="400" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="540" y1="48" x2="540" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="400" y1="54" x2="540" y2="54" stroke="#111" strokeWidth="2" />
                <line x1="400" y1="66" x2="540" y2="66" stroke="#111" strokeWidth="2" />

                <line x1="640" y1="60" x2="780" y2="60" stroke="#111" strokeWidth="14" />
                <line x1="640" y1="48" x2="640" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="780" y1="48" x2="780" y2="72" stroke="#111" strokeWidth="3" />
                <line x1="640" y1="54" x2="780" y2="54" stroke="#111" strokeWidth="2" />
                <line x1="640" y1="66" x2="780" y2="66" stroke="#111" strokeWidth="2" />

                {/* 左壁 */}
                <line x1="80" y1="310" x2="80" y2="440" stroke="#111" strokeWidth="14" />
                <line x1="68" y1="310" x2="92" y2="310" stroke="#111" strokeWidth="3" />
                <line x1="68" y1="440" x2="92" y2="440" stroke="#111" strokeWidth="3" />
                <line x1="74" y1="310" x2="74" y2="440" stroke="#111" strokeWidth="2" />
                <line x1="86" y1="310" x2="86" y2="440" stroke="#111" strokeWidth="2" />

                {/* 右壁 */}
                <line x1="820" y1="100" x2="820" y2="240" stroke="#111" strokeWidth="14" />
                <line x1="808" y1="100" x2="832" y2="100" stroke="#111" strokeWidth="3" />
                <line x1="808" y1="240" x2="832" y2="240" stroke="#111" strokeWidth="3" />
                <line x1="814" y1="100" x2="814" y2="240" stroke="#111" strokeWidth="2" />
                <line x1="826" y1="100" x2="826" y2="240" stroke="#111" strokeWidth="2" />

                <line x1="820" y1="380" x2="820" y2="520" stroke="#111" strokeWidth="14" />
                <line x1="808" y1="380" x2="832" y2="380" stroke="#111" strokeWidth="3" />
                <line x1="808" y1="520" x2="832" y2="520" stroke="#111" strokeWidth="3" />
                <line x1="814" y1="380" x2="814" y2="520" stroke="#111" strokeWidth="2" />
                <line x1="826" y1="380" x2="826" y2="520" stroke="#111" strokeWidth="2" />

                {/* 下壁 */}
                <line x1="120" y1="840" x2="280" y2="840" stroke="#111" strokeWidth="14" />
                <line x1="120" y1="828" x2="120" y2="852" stroke="#111" strokeWidth="3" />
                <line x1="280" y1="828" x2="280" y2="852" stroke="#111" strokeWidth="3" />
                <line x1="120" y1="834" x2="280" y2="834" stroke="#111" strokeWidth="2" />
                <line x1="120" y1="846" x2="280" y2="846" stroke="#111" strokeWidth="2" />

                <line x1="620" y1="840" x2="780" y2="840" stroke="#111" strokeWidth="14" />
                <line x1="620" y1="828" x2="620" y2="852" stroke="#111" strokeWidth="3" />
                <line x1="780" y1="828" x2="780" y2="852" stroke="#111" strokeWidth="3" />
                <line x1="620" y1="834" x2="780" y2="834" stroke="#111" strokeWidth="2" />
                <line x1="620" y1="846" x2="780" y2="846" stroke="#111" strokeWidth="2" />

                {/* ===== 階段 ===== */}
                <g>
                  <rect x="580" y="60" width="240" height="110" stroke="#111" strokeWidth="4" fill="none" />
                  {[0,1,2,3,4,5,6,7,8,9,10].map((i) => (
                    <line key={i} x1="580" y1={60 + i * 10} x2="820" y2={60 + i * 10} stroke="#111" strokeWidth="1.5" />
                  ))}
                  <line x1="700" y1="60" x2="700" y2="170" stroke="#111" strokeWidth="3" strokeDasharray="8 4" />
                </g>

                {/* ===== 寸法線 ===== */}
                {/* 全体幅 */}
                <line x1="80" y1="20" x2="820" y2="20" stroke="#111" strokeWidth="1.5" strokeDasharray="8 5" />
                <line x1="80" y1="10" x2="80" y2="30" stroke="#111" strokeWidth="2.5" />
                <line x1="820" y1="10" x2="820" y2="30" stroke="#111" strokeWidth="2.5" />
                {/* 全体高さ */}
                <line x1="860" y1="60" x2="860" y2="840" stroke="#111" strokeWidth="1.5" strokeDasharray="8 5" />
                <line x1="850" y1="60" x2="870" y2="60" stroke="#111" strokeWidth="2.5" />
                <line x1="850" y1="840" x2="870" y2="840" stroke="#111" strokeWidth="2.5" />
                {/* 部分寸法 */}
                <line x1="80" y1="880" x2="340" y2="880" stroke="#111" strokeWidth="1" strokeDasharray="5 4" />
                <line x1="80" y1="872" x2="80" y2="888" stroke="#111" strokeWidth="2" />
                <line x1="340" y1="872" x2="340" y2="888" stroke="#111" strokeWidth="2" />
                <line x1="340" y1="880" x2="580" y2="880" stroke="#111" strokeWidth="1" strokeDasharray="5 4" />
                <line x1="580" y1="872" x2="580" y2="888" stroke="#111" strokeWidth="2" />
                <line x1="580" y1="880" x2="820" y2="880" stroke="#111" strokeWidth="1" strokeDasharray="5 4" />
                <line x1="820" y1="872" x2="820" y2="888" stroke="#111" strokeWidth="2" />

                {/* ===== 中心線（一点鎖線） ===== */}
                <line x1="210" y1="60" x2="210" y2="280" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="80" y1="170" x2="340" y2="170" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="460" y1="170" x2="580" y2="170" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="210" y1="280" x2="210" y2="560" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="80" y1="420" x2="340" y2="420" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="460" y1="420" x2="580" y2="420" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="700" y1="280" x2="700" y2="560" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="580" y1="420" x2="820" y2="420" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="210" y1="560" x2="210" y2="840" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="80" y1="700" x2="340" y2="700" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="700" y1="560" x2="700" y2="840" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
                <line x1="580" y1="700" x2="820" y2="700" stroke="#111" strokeWidth="1" strokeDasharray="12 4 2 4" />
              </svg>

              {/* Top-left typography block */}
              <div className="absolute top-6 left-6 z-10">
                <FoundedYearDisplay active={visible} endYear={timelineYear} />
              </div>

              {anniversaryMilestone != null ? <AnniversaryBadge milestone={anniversaryMilestone} /> : null}

            </div>
          </div>
          </div>
        </SubPageSurface>
      </div>
    </SubPageSection>
  );
}
